import classNames from "classnames";
import isString from "lodash/isString";
import uniqueId from "lodash/uniqueId";
import { ErrorMessage } from "PFComponents/error_message/error_message";
import React, { AriaAttributes, ChangeEvent, ReactNode, useEffect, useRef, useState } from "react";

import { Typography } from "../typography";
import css from "./checkbox.module.scss";
import { CheckboxIcon } from "./checkbox_icon";

export type CheckboxProps = {
  checked: boolean;
  onChange?: (value: boolean, event?: ChangeEvent<HTMLInputElement>) => void;
  // The label should be string. The ReactNode type is reserved for the MultiSelect component.
  label?: string | ReactNode;
  disabled?: boolean;
  id?: string;
  indeterminate?: boolean;
  vertical?: boolean;
  className?: string;
  errorMessage?: string;
  tabIndex?: number;
  qaId?: string;
  clipLabelOverflow?: boolean;
} & AriaAttributes;

export const Checkbox = ({
  label,
  checked,
  disabled,
  onChange,
  id,
  indeterminate,
  vertical,
  className,
  "aria-label": ariaLabel,
  errorMessage,
  tabIndex,
  qaId,
  clipLabelOverflow = true
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const checkboxId = useRef(id || uniqueId("checkbox"));
  const [internalIndeterminate, setInternalIndeterminate] = useState(!!indeterminate);
  const [internalChecked, setInternalChecked] = useState(checked);

  useEffect(() => {
    setInternalIndeterminate(!!indeterminate);
  }, [indeterminate]);

  useEffect(() => {
    setInternalChecked(checked);
  }, [checked]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = internalIndeterminate;
    }
  }, [inputRef.current, internalIndeterminate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInternalIndeterminate(false);
    setInternalChecked(event.target.checked);
    onChange && onChange(event.target.checked, event);
  };

  return (
    <div className={css.root}>
      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <label
        data-qa-id={qaId || "checkbox-container"}
        className={classNames(css.label, className, {
          [css.disabled]: disabled,
          [css.vertical]: vertical
        })}
        onClick={(event) => event.stopPropagation()}
      >
        <input
          id={checkboxId.current}
          ref={inputRef}
          type="checkbox"
          checked={internalChecked}
          disabled={disabled}
          onChange={handleChange}
          className={classNames(css.input, {
            [css.checked]: internalChecked
          })}
          aria-label={ariaLabel || (isString(label) ? label : "")}
          aria-checked={internalIndeterminate ? "mixed" : internalChecked}
          tabIndex={tabIndex}
        />
        <div className={css.customCheckbox}>
          <CheckboxIcon checked={internalChecked} indeterminate={internalIndeterminate} />
        </div>
        {label && (
          <div className={classNames({ [css.labelContainer]: !vertical })}>
            {isString(label) ? (
              <Typography
                withMargin
                variant="bodyRegular"
                tag="span"
                qaId="checkbox-label"
                className={classNames({ [css.verticalLabel]: vertical })}
                textOverflow={clipLabelOverflow && "clip"}
              >
                {label}
              </Typography>
            ) : (
              label
            )}
          </div>
        )}
      </label>
      {errorMessage && <ErrorMessage iconSize="sm" message={errorMessage} className={css.errorMessage} />}
    </div>
  );
};
